import { useEffect, useMemo, useState } from 'react';
import { specialLabelsFor } from './specialDates';
import { sheetUrl, searchUrl, todayCST } from './config';
import { useTheme } from './theme';

// Members allergy lookup (memberNumber -> combined allergy display string).
// Combines the chosen allergy list with the freeform allergyNote in parens
// so kitchen staff see the full picture (especially for "Other").
const loadAllergyMap = async () => {
  try {
    const res = await fetch(sheetUrl('Members'), { cache: 'no-store' });
    const members = await res.json();
    const map = {};
    (members || []).forEach((m) => {
      const key = String(m.memberNumber || '').trim();
      const allergy = String(m.allergy || '').trim();
      const note = String(m.allergyNote || '').trim();
      if (!key) return;
      const combined = allergy && note
        ? `${allergy} (${note})`
        : allergy || (note ? `Other (${note})` : '');
      if (combined) map[key] = combined;
    });
    return map;
  } catch (e) {
    console.error('KitchenDashboard: failed to load Members allergies', e);
    return {};
  }
};

export default function KitchenDashboard() {
  // Theme (shared across the app)
  const [theme, setTheme] = useTheme();

  // Mobile stacking
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 760);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const S = stylesFor(theme);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Allergy lookup
  const [allergyByMember, setAllergyByMember] = useState({});
  useEffect(() => {
    (async () => {
      const map = await loadAllergyMap();
      setAllergyByMember(map);
    })();
  }, []);

  // Menu state (view-only)
  const [menu, setMenu] = useState({ breakfast: '', lunch: '', dinner: '', notes: '' });
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuMsg, setMenuMsg] = useState('');

  const [selectedDate, setSelectedDate] = useState(todayCST());

  // Fetch bookings + menu for selected date (read-only)
  const fetchRowsForDate = async (date) => {
    setLoading(true);
    setMenuLoading(true);
    setMenuMsg('');
    try {
      const bookingsRes = await fetch(sheetUrl('Sheet1'), { cache: 'no-store' });
      const bookingsData = await bookingsRes.json();
      const filtered = (bookingsData || []).filter(
        (row) =>
          row.date === date &&
          !(row.memberNumber || '').toString().startsWith('CD-')
      );
      setRows(filtered);

      const menuRes = await fetch(searchUrl('Menu', { date }), { cache: 'no-store' });
      const menuData = await menuRes.json();
      const row = menuData && menuData.length ? menuData[0] : null;
      if (row) {
        setMenu({
          breakfast: row.breakfast || '',
          lunch: row.lunch || '',
          dinner: row.dinner || '',
          notes: row.chefs_note || '',
        });
        setMenuMsg('');
      } else {
        setMenu({ breakfast: '', lunch: '', dinner: '', notes: '' });
        setMenuMsg('No menu set for this date yet.');
      }
    } catch (err) {
      console.error('KitchenDashboard: Error fetching data', err);
      setRows([]);
      setMenu({ breakfast: '', lunch: '', dinner: '', notes: '' });
      setMenuMsg('Failed to load data.');
    } finally {
      setLoading(false);
      setMenuLoading(false);
    }
  };

  useEffect(() => {
    fetchRowsForDate(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Per-meal counts
  const mealCounts = useMemo(() => {
    const init = {
      breakfast: { adults: 0, children: 0, guests: 0, total: 0 },
      lunch: { adults: 0, children: 0, guests: 0, total: 0 },
      dinner: { adults: 0, children: 0, guests: 0, total: 0 },
    };
    rows.forEach((r) => {
      const name = (r.name || '').toString();
      const isChild = /\(c\)/i.test(name);
      const isGuest = /\(g\)/i.test(name);
      ['breakfast', 'lunch', 'dinner'].forEach((meal) => {
        if ((r[meal] || '').toLowerCase() === 'yes') {
          if (isChild) {
            init[meal].children += 1;
          } else {
            init[meal].adults += 1;
            if (isGuest) init[meal].guests += 1;
          }
          init[meal].total += 1;
        }
      });
    });
    return init;
  }, [rows]);

  // Day totals
  const dayTotals = useMemo(() => {
    const meals = Object.values(mealCounts);
    const sum = (key) => meals.reduce((acc, m) => acc + m[key], 0);
    return {
      adults: sum('adults'),
      children: sum('children'),
      guests: sum('guests'),
      total: sum('total'),
    };
  }, [mealCounts]);

  // Allergies today
  const allergyCounts = useMemo(() => {
    const counts = {};
    rows.forEach((r) => {
      const key = String(r.memberNumber || '').trim();
      const allergy = allergyByMember[key];
      if (!allergy) return;
      allergy
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean)
        .forEach((a) => {
          counts[a] = (counts[a] || 0) + 1;
        });
    });
    return counts;
  }, [rows, allergyByMember]);

  const allergySummary = useMemo(() => {
    const entries = Object.entries(allergyCounts).sort((a, b) => b[1] - a[1]);
    return entries.map(([a, n]) => `${a} (${n})`).join(', ');
  }, [allergyCounts]);

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.headerRow}>
        <div>
          <div style={S.hTitle}>Kitchen Dashboard</div>
          <div style={S.hSub}>Meal counts • Menu • Allergies</div>
        </div>
        <div style={S.headerRight}>
          <button
            style={S.themeBtn}
            onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
          >
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </button>
          <button
            style={S.logoutBtn}
            onClick={() => {
              localStorage.removeItem('authenticatedUser');
              window.location.reload();
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Date + refresh */}
      <div style={S.controlsRow}>
        <div style={S.controlGroup}>
          <label style={S.label}>Date</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={S.dateInput}
          />
        </div>
        <button style={S.refreshBtn} onClick={() => fetchRowsForDate(selectedDate)}>
          Refresh
        </button>
      </div>

      {/* Menu display */}
      <div style={{ ...S.card, marginTop: 12 }}>
        <div style={S.menuHeader}>
          <div style={S.cardTitle}>Menu — {selectedDate}</div>
          <div style={S.muted}>{menuLoading ? 'Loading…' : menuMsg}</div>
        </div>
        <div style={{ marginTop: 8 }}>
          <div>
            <b>Breakfast:</b>{' '}
            {menu.breakfast ? menu.breakfast : <span style={S.muted}>No breakfast menu set.</span>}
          </div>
          <div style={{ marginTop: 6 }}>
            <b>Lunch:</b>{' '}
            {menu.lunch ? menu.lunch : <span style={S.muted}>No lunch menu set.</span>}
          </div>
          <div style={{ marginTop: 6 }}>
            <b>Dinner:</b>{' '}
            {menu.dinner ? menu.dinner : <span style={S.muted}>No dinner menu set.</span>}
          </div>
          {menu.notes ? (
            <div style={{ marginTop: 8 }}>
              <b>Notes:</b> {menu.notes}
            </div>
          ) : null}
        </div>

        {allergySummary ? (
          <div style={{ marginTop: 10 }}>
            <span style={S.allergyPill}>⚠ Allergies present today: {allergySummary}</span>
          </div>
        ) : null}
      </div>

      {/* Per-meal summary cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: 12,
          marginTop: 12,
        }}
      >
        {['breakfast', 'lunch', 'dinner'].map((meal) => {
          const spec = specialLabelsFor(selectedDate);
          const label = spec.label[meal];
          const enabled = spec.enabled[meal];
          return (
            <div key={meal} style={{ ...S.card, opacity: enabled ? 1 : 0.5 }}>
              <div style={S.cardTitle}>{label ? label : '(Not offered)'}</div>
              <div style={S.countRow}>Adults: <b>{mealCounts[meal].adults}</b></div>
              <div style={S.countRow}>Children: <b>{mealCounts[meal].children}</b></div>
              <div style={S.countRow}>Guests: <b>{mealCounts[meal].guests}</b></div>
              <div style={S.countRow}>Total: <b>{mealCounts[meal].total}</b></div>
            </div>
          );
        })}
      </div>

      {/* Day totals */}
      <div style={{ ...S.card, marginTop: 12 }}>
        <div style={S.cardTitle}>Day Totals</div>
        <div style={S.countRow}>Adults: <b>{dayTotals.adults}</b></div>
        <div style={S.countRow}>Children: <b>{dayTotals.children}</b></div>
        <div style={S.countRow}>Guests: <b>{dayTotals.guests}</b></div>
        <div style={S.countRow}>Total: <b>{dayTotals.total}</b></div>
      </div>

      {/* Bookings list (read-only) */}
      <div style={{ ...S.card, marginTop: 12 }}>
        <div style={S.cardTitle}>Bookings — {selectedDate}</div>
        {loading ? (
          <div style={S.muted}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={S.muted}>No meals booked for this date.</div>
        ) : (
          rows.map((row) => {
            const rowSpec = specialLabelsFor(row.date);
            const allergy = allergyByMember[String(row.memberNumber || '').trim()];
            return (
              <div key={row.id} style={S.bookingRow}>
                <div style={S.bookingTitle}>
                  {row.name}{' '}
                  <span style={S.muted}>({row.memberNumber || ''})</span>
                  {allergy ? (
                    <span style={S.allergyPill}>⚠ Allergy: {allergy}</span>
                  ) : null}
                </div>
                <div style={S.mealBadges}>
                  {['breakfast', 'lunch', 'dinner'].map((meal) => {
                    const labelText = rowSpec.label[meal];
                    const enabled = rowSpec.enabled[meal];
                    if (!labelText) return null;
                    const checked = (row[meal] || '').toLowerCase() === 'yes';
                    if (!enabled) {
                      return (
                        <span key={meal} style={{ ...S.mealPill, opacity: 0.4 }}>
                          {labelText}: —
                        </span>
                      );
                    }
                    return (
                      <span
                        key={meal}
                        style={{ ...S.mealPill, opacity: checked ? 1 : 0.55 }}
                      >
                        {labelText}: {checked ? 'Yes' : 'No'}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const stylesFor = (theme) => {
  const dark = theme === 'dark';
  return {
    page: {
      padding: 20,
      fontFamily: 'Arial',
      background: dark ? '#0f172a' : '#f8fafc',
      color: dark ? '#e5e7eb' : '#0f172a',
      minHeight: '100vh',
    },
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      gap: 12,
      flexWrap: 'wrap',
    },
    headerRight: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
    hTitle: { fontSize: 22, fontWeight: 900, letterSpacing: 0.2 },
    hSub: { fontSize: 12, opacity: 0.75, marginTop: 2 },
    muted: { color: dark ? '#94a3b8' : '#64748b', fontSize: 12 },
    allergyPill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      marginLeft: 8,
      padding: '3px 10px',
      borderRadius: 999,
      border: dark
        ? '1px solid rgba(253, 224, 71, 0.35)'
        : '1px solid rgba(146, 64, 14, 0.25)',
      background: dark ? 'rgba(253, 224, 71, 0.10)' : 'rgba(251, 191, 36, 0.18)',
      color: dark ? '#fde68a' : '#92400e',
      fontSize: 12,
      fontWeight: 800,
      maxWidth: '100%',
    },
    controlsRow: { display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 12 },
    controlGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
    label: { fontSize: 12, opacity: 0.8, fontWeight: 700 },
    dateInput: {
      padding: '10px 12px',
      borderRadius: 12,
      border: dark ? '1px solid #334155' : '1px solid #cbd5e1',
      background: dark ? '#0b1220' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      outline: 'none',
      minWidth: 160,
    },
    card: {
      border: dark ? '1px solid rgba(148,163,184,0.22)' : '1px solid #e2e8f0',
      borderRadius: 16,
      padding: 14,
      background: dark ? 'rgba(15,23,42,0.55)' : '#ffffff',
      boxShadow: dark
        ? '0 10px 30px rgba(0,0,0,0.25)'
        : '0 8px 22px rgba(15,23,42,0.06)',
    },
    cardTitle: { fontWeight: 900, marginBottom: 8 },
    countRow: { marginTop: 4, fontSize: 14 },
    menuHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 10,
      flexWrap: 'wrap',
    },
    bookingRow: {
      borderTop: dark
        ? '1px dashed rgba(148,163,184,0.25)'
        : '1px dashed #e2e8f0',
      paddingTop: 12,
      marginTop: 12,
    },
    bookingTitle: {
      fontWeight: 900,
      fontSize: 15,
      lineHeight: 1.25,
      wordBreak: 'break-word',
    },
    mealBadges: { display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 },
    mealPill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '10px 12px',
      borderRadius: 999,
      border: dark ? '1px solid #334155' : '1px solid #cbd5e1',
      background: dark ? 'rgba(2,6,23,0.35)' : '#f8fafc',
      userSelect: 'none',
      fontWeight: 800,
      fontSize: 13,
      whiteSpace: 'nowrap',
    },
    refreshBtn: {
      padding: '10px 14px',
      borderRadius: 12,
      border: dark ? '1px solid #334155' : '1px solid #cbd5e1',
      background: dark ? 'rgba(2,6,23,0.35)' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontWeight: 900,
      cursor: 'pointer',
    },
    themeBtn: {
      padding: '10px 14px',
      borderRadius: 12,
      border: dark ? '1px solid #334155' : '1px solid #cbd5e1',
      background: dark ? 'rgba(2,6,23,0.35)' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontWeight: 900,
      cursor: 'pointer',
    },
    logoutBtn: {
      padding: '10px 14px',
      borderRadius: 12,
      border: dark ? '1px solid rgba(148,163,184,0.25)' : '1px solid #cbd5e1',
      background: dark ? 'rgba(2,6,23,0.35)' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontWeight: 900,
      cursor: 'pointer',
    },
  };
};
